import React, { useEffect } from 'react';
import { Outlet, useLocation, useMatch } from 'react-router';
import { Capacitor } from '@capacitor/core';

// Utils
import { DEBT_GOAL } from '../../../components/Optimizer/types';
import { usePageChange } from '../../../hooks/usePageChange';
import {
  useOnboardingContext,
  useOnboardingDispatch,
} from '../../../providers/onboarding/onboarding.provider';
import {
  setSource,
  setGoal,
  setLoanInterestStatus,
} from '../../../providers/onboarding/onboarding.actions';
import AppUrlListener from '../../../utils/deeplink.utils';
import { getCampaignFromParams } from '../../../utils';
import getStorage from '../../../utils/storage.utils';

export const WrapperLayout = () => {
  const storage = getStorage();

  const { search } = useLocation();
  const params = new URLSearchParams(search);

  usePageChange();

  const source = params.get('utm_content');
  const isCio = params.get('cio');
  const gclid = params.get('gclid');
  const fbclid = params.get('fbclid');
  const ref = params.get('ref');
  const maximizeHome = params.get('maximizeHome') || params.get('homeMaximize');
  const goalParam = params.get('goal');
  const loanInterestParam = params.get('loanInterest');
  const match = useMatch('/cash');
  const campaign:
    | 'credit-score'
    | 'consolidation'
    | 'save'
    | 'ham'
    | 'cash'
    | string = match ? 'cash' : getCampaignFromParams(params) || '';

  const medium = params.get('utm_medium');

  const state = useOnboardingContext();
  const dispatch = useOnboardingDispatch();

  useEffect(() => {
    const setStorage = async () => {
      const activeSource = await storage.getItem('solve_source');
      if (activeSource && state.source !== activeSource) {
        dispatch(setSource(activeSource));
      }

      if (source) {
        await storage.setItem('solve_source', source);
        await storage.setItem('utm_content', source);
      } else if (isCio) {
        await storage.setItem('utm_content', 'customer.io');
      }

      if (medium) {
        await storage.setItem('utm_medium', medium);
      }

      if (ref) {
        await storage.setItem('referral_uid', ref);
      }

      const hasReferrer = await storage.getItem('referrer');
      if (!hasReferrer && document.referrer) {
        await storage.setItem('referrer', document.referrer);
      }

      // Persist click IDs early so they survive even if the user bounces
      if (gclid) {
        const existingGclid = await storage.getItem('gclid');
        if (!existingGclid) {
          await storage.setItem('gclid', gclid);
          await storage.setItem('gclid_timestamp', Date.now().toString());
        }
      }

      if (fbclid) {
        const existingFbclid = await storage.getItem('fbclid');
        if (!existingFbclid) {
          await storage.setItem('fbclid', fbclid);
          await storage.setItem('fbclid_timestamp', Date.now().toString());
          await storage.setItem('fbc', `fb.1.${Date.now()}.${fbclid}`);
        }
      }
    };

    setStorage();
  }, [source, medium, ref, gclid, fbclid, document.referrer]);

  useEffect(() => {
    const setStorage = async () => {
      if (maximizeHome === 'true') {
        dispatch(setGoal({ goal: DEBT_GOAL.MAX_HOME }));
        await storage.setItem('solve_goal', DEBT_GOAL.MAX_HOME);
      }
    };

    setStorage();
  }, [maximizeHome]);

  useEffect(() => {
    const setStorage = async () => {
      if (campaign === 'cash') {
        dispatch(setGoal({ goal: DEBT_GOAL.GET_CASH }));
        dispatch(setLoanInterestStatus({ loanInterestStatus: true }));
        await storage.setItem('solve_goal', DEBT_GOAL.GET_CASH);
      }
    };

    setStorage();
  }, [campaign]);

  // Generic goal / loan-interest hydration from the querystring. Lets entry points
  // (e.g. the PR "Products" nav links) pre-select an onboarding goal and loan-interest
  // status by linking to `/onboarding?goal=GET_CASH&loanInterest=true`, which then
  // skips the corresponding onboarding steps via getActiveOnboardingStep.
  useEffect(() => {
    const hydrateGoal = async () => {
      if (goalParam && Object.values(DEBT_GOAL).includes(goalParam as DEBT_GOAL)) {
        dispatch(setGoal({ goal: goalParam }));
        await storage.setItem('solve_goal', goalParam);
      }

      if (loanInterestParam !== null) {
        dispatch(setLoanInterestStatus({ loanInterestStatus: loanInterestParam === 'true' }));
      }
    };

    hydrateGoal();
  }, [goalParam, loanInterestParam]);

  return (
    <>
      {Capacitor.isNativePlatform() && <AppUrlListener />}
      <Outlet />
    </>
  );
};
